import { AxiosRequestConfig } from 'axios'
import { axiosWithImitator } from '../withAuth'
import { SortCaseProperty } from '../../utils/sortCasesHelper'
import { CaseReportingList } from '../../models/CaseReportingList'
import { isDummyUser } from '../../utils/utils'

// Helper function to parse JWT token (copied from withAuth.ts)
function parseJwt(token: string): any {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
        window
            .atob(base64)
            .split('')
            .map(function (c: any) {
                return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`
            })
            .join('')
    )

    return JSON.parse(jsonPayload)
}

// Function to get salary base URL based on user authentication
const getSalaryBaseURL = (): string => {
    const userData = localStorage.getItem('user_data')

    if (!userData) {
        return process.env.REACT_APP_SALARY_BASE_URL || ''
    }

    try {
        const user = JSON.parse(userData)
        const jwt = parseJwt(user.access_token)
        const { upn } = jwt
        const isDummyUserFlag = isDummyUser(upn)

        // If user is dummy user, return dummy salary base URL
        if (isDummyUserFlag) {
            return process.env.REACT_APP_SALARY_BASE_URL_DUMMY_USER || ''
        }

        return process.env.REACT_APP_SALARY_BASE_URL || ''
    } catch (error) {
        // Fallback to default base URL if parsing fails
        return process.env.REACT_APP_SALARY_BASE_URL || ''
    }
}

// Get the base URL dynamically - call the function each time instead of caching
const getBaseURL = (): string => getSalaryBaseURL()

interface CustomAxiosConfig extends AxiosRequestConfig {
    skipBaseURLOverride?: boolean
    useImitatorForSalaryApi?: boolean
}

const getSalaryData = async ({
    sortCaseProperty = {
        property: 'Default',
        direction: 'Default',
    },
}: {
    sortCaseProperty?: SortCaseProperty
} = {}): Promise<any> => {
    // Real API implementation
    const response = await axiosWithImitator.get(
        '/SagHent/HentMineLoenindplaceringSagList',
        {
            baseURL: getBaseURL(),
            params: {
                Sorteringsfelt: sortCaseProperty.property,
                Sorteringsretning: sortCaseProperty.direction,
            },
            skipBaseURLOverride: true,
            useImitatorForSalaryApi: true,
        } as CustomAxiosConfig
    )
    return response.data
}

export type GetClosedCasesParams = {
    sortCaseProperty?: SortCaseProperty
    pageSize?: number
}

const getClosedCases = async ({
    sortCaseProperty = {
        property: 'Default',
        direction: 'Default',
    },
    pageSize = 20,
}: GetClosedCasesParams = {}): Promise<any> => {
    const response = await axiosWithImitator.get(
        '/SagHent/HentAfluttetSagList',
        {
            baseURL: getBaseURL(),
            params: {
                Sorteringsfelt: sortCaseProperty.property,
                Sorteringsretning: sortCaseProperty.direction,
                PageSize: pageSize,
            },
            skipBaseURLOverride: true,
            useImitatorForSalaryApi: true,
        } as CustomAxiosConfig
    )
    return response.data
}

const getNextClosedCasesPage = async (
    page: number,
    caseListId: string
): Promise<any> => {
    const response = await axiosWithImitator.get(
        '/SagHent/HentAfluttetSagListNextPages',
        {
            baseURL: getBaseURL(),
            params: {
                Page: page,
                SagListId: caseListId,
            },
            skipBaseURLOverride: true,
            useImitatorForSalaryApi: true,
        } as CustomAxiosConfig
    )
    return response.data
}

const getCaseDetails = async (id: string): Promise<any> => {
    // Real API implementation
    const response = await axiosWithImitator.get('/SagHent/HentSag', {
        params: {
            SagsId: id,
        },
        baseURL: getBaseURL(),
        skipBaseURLOverride: true,
        useImitatorForSalaryApi: true,
    } as CustomAxiosConfig)

    if ((response as any).error && (response as any).error.status === 400) {
        return { error: (response as any).error }
    }
    return response.data
}

const takeCase = async (id: string, regTime: string): Promise<any> => {
    const response = await axiosWithImitator.put(
        '/SagOpdater/AccepterSag',
        {
            SagsId: id,
            sagsRegTid: regTime,
        },
        {
            baseURL: getBaseURL(),
            skipBaseURLOverride: true,
            useImitatorForSalaryApi: true,
        } as CustomAxiosConfig
    )
    return response.data
}

const getRejectCaseReasonList = async (): Promise<any> => {
    // Real API implementation
    const response = await axiosWithImitator.get(
        '/OplagsdataConstroller/HentAfvisAarsagTypeList',
        {
            baseURL: getBaseURL(),
            skipBaseURLOverride: true,
            useImitatorForSalaryApi: true,
        } as CustomAxiosConfig
    )

    return response
}

// /SagOpdater/TestSammenfletMedAndenSagHentOverlevedendeSag
const tryAttachCase = async ({
    caseId,
    caseRegTime,
    otherCaseId,
    otherCaseRegTime,
}: {
    caseId: string
    caseRegTime: string
    otherCaseId: string
    otherCaseRegTime: string
}): Promise<any> => {
    const response = await axiosWithImitator.post(
        '/SagOpdater/TestSammenfletMedAndenSagHentOverlevedendeSag',
        {
            sagsId: caseId,
            sagsRegTid: caseRegTime,
            andenSag: {
                sagsId: otherCaseId,
                sagsRegTid: otherCaseRegTime,
            },
        },
        {
            baseURL: getBaseURL(),
            skipBaseURLOverride: true,
            useImitatorForSalaryApi: true,
        } as CustomAxiosConfig
    )
    return response.data
}

const attachCase = async ({
    caseId,
    caseRegTime,
    otherCaseId,
    otherCaseRegTime,
}: {
    caseId: string
    caseRegTime: string
    otherCaseId: string
    otherCaseRegTime: string
}): Promise<any> => {
    const response = await axiosWithImitator.put(
        '/SagOpdater/SammenfletMedAndenSag',
        {
            sagsId: caseId,
            sagsRegTid: caseRegTime,
            andenSag: {
                sagsId: otherCaseId,
                sagsRegTid: otherCaseRegTime,
            },
        },
        {
            baseURL: getBaseURL(),
            skipBaseURLOverride: true,
            useImitatorForSalaryApi: true,
        } as CustomAxiosConfig
    )
    return response.data
}

const removeMergedCase = async ({
    caseId,
    caseRegTime,
    otherCaseId,
    otherCaseRegTime,
}: {
    caseId: string
    caseRegTime: string
    otherCaseId: string
    otherCaseRegTime: string
}): Promise<any> => {
    const response = await axiosWithImitator.delete(
        '/SagOpdater/FjernSammenfletMedAndenSag',
        {
            data: {
                sagsId: caseId,
                sagsRegTid: caseRegTime,
                andenSag: {
                    sagsId: otherCaseId,
                    sagsRegTid: otherCaseRegTime,
                },
            },
            baseURL: getBaseURL(),
            skipBaseURLOverride: true,
            useImitatorForSalaryApi: true,
        } as CustomAxiosConfig
    )
    return response.data
}

const rejectCase = async (
    id: string,
    regTime: string,
    rejectReason: string,
    freeText: string
): Promise<any> => {
    // Real API implementation
    const response = await axiosWithImitator.put(
        '/SagOpdater/AfvisSag',
        {
            sagsId: id,
            sagsRegTid: regTime,
            afvisAarsagNr: rejectReason,
            afvisAarsagFriTekst: freeText,
        },
        {
            baseURL: getBaseURL(),
            skipBaseURLOverride: true,
            useImitatorForSalaryApi: true,
        } as CustomAxiosConfig
    )
    return response
}

const updateCaseMobileNumber = async (
    id: string,
    regTime: string,
    mobilNumber: string
): Promise<any> => {
    const response = await axiosWithImitator.put(
        '/SagOpdater/OpdaterNyansatteMobil',
        {
            SagsId: id,
            sagsRegTid: regTime,
            mobilNR: mobilNumber,
        },
        {
            baseURL: getBaseURL(),
            skipBaseURLOverride: true,
            useImitatorForSalaryApi: true,
        } as CustomAxiosConfig
    )
    return response
}
const updateCaseEmail = async (
    id: string,
    regTime: string,
    emailAddress: string
): Promise<any> => {
    const response = await axiosWithImitator.put(
        '/SagOpdater/OpdaterNyansatteEmail',
        {
            SagsId: id,
            sagsRegTid: regTime,
            email: emailAddress,
        },
        {
            baseURL: getBaseURL(),
            skipBaseURLOverride: true,
            useImitatorForSalaryApi: true,
        } as CustomAxiosConfig
    )
    return response
}
const updateCaseBirthdate = async (
    id: string,
    regTime: string,
    birthday: string
): Promise<any> => {
    const response = await axiosWithImitator.put(
        '/SagOpdater/OpdaterNyansatteFoedselsdato',
        {
            SagsId: id,
            sagsRegTid: regTime,
            foedselsDato: birthday,
        },
        {
            baseURL: getBaseURL(),
            skipBaseURLOverride: true,
            useImitatorForSalaryApi: true,
        } as CustomAxiosConfig
    )
    return response
}
const updateCaseHireDate = async (
    id: string,
    regTime: string,
    hireDate: string
): Promise<any> => {
    const response = await axiosWithImitator.put(
        '/SagOpdater/OpdaterNyansatteAnsaettelsesdato',
        {
            SagsId: id,
            sagsRegTid: regTime,
            ansaettelsesDato: hireDate,
        },
        {
            baseURL: getBaseURL(),
            skipBaseURLOverride: true,
            useImitatorForSalaryApi: true,
        } as CustomAxiosConfig
    )
    return response
}

// Mail/HentTrSkabelonMailList
const getMailTemplateList = async (id: string): Promise<any> => {
    const response = await axiosWithImitator.post(
        '/api/Mail/HentTrSkabelonMailList',
        {
            sagsId: id,
        },
        {
            baseURL: getBaseURL(),
            skipBaseURLOverride: true,
            useImitatorForSalaryApi: true,
        } as CustomAxiosConfig
    )
    return response.data
}

// Mail/SendMailFraTr
export type SendMailParams = {
    subject: string
    content: string
    to: string[]
    cc: string[]
    bcc: string[]
    template: string
    id: string
    attachments: {
        fileName: string
        caseNumber: string
    }[]
}

const sendMail = async ({
    subject,
    content,
    to,
    cc,
    bcc,
    template,
    id,
    attachments = [],
}: SendMailParams): Promise<any> => {
    const response = await axiosWithImitator.post(
        '/api/Mail/SendMailFraTr',
        {
            sagsId: id,
            skabelonType: template,
            emne: subject,
            indhold: content,
            tilEmailAdresseList: to,
            ccEmailAdresseList: cc,
            bccEmailAdresseList: bcc,
            vedhaeftetFilIdList: attachments?.map((attachment) => ({
                fuldeNavn: attachment.fileName,
                sagsNr: attachment.caseNumber,
            })),
        },
        {
            baseURL: getBaseURL(),
            skipBaseURLOverride: true,
            useImitatorForSalaryApi: true,
        } as CustomAxiosConfig
    )
    return response
}

// /OplagsdataConstroller/HentReminderAarsagTypeList
const getReminderReasonList = async (): Promise<any> => {
    const response = await axiosWithImitator.get(
        '/OplagsdataConstroller/HentReminderAarsagTypeList',
        {
            baseURL: getBaseURL(),
            skipBaseURLOverride: true,
            useImitatorForSalaryApi: true,
        } as CustomAxiosConfig
    )
    return response.data
}

export type CreateReminderType = {
    caseId: string
    caseRegTime: string
    reminderDate: string
    reminderReason: number
    reminderFreeText: string
    sendReminderEmail: boolean
}

const createReminder = async ({
    caseId,
    caseRegTime,
    reminderDate,
    reminderReason,
    reminderFreeText,
    sendReminderEmail,
}: {
    caseId: string
    caseRegTime: string
    reminderDate: string
    reminderReason: number
    reminderFreeText: string
    sendReminderEmail: boolean
}): Promise<any> => {
    const response = await axiosWithImitator.post(
        '/Reminder/OpretReminder',
        {
            sagsId: caseId,
            sagsRegTid: caseRegTime,
            reminderDato: reminderDate,
            reminderAarsagNr: reminderReason,
            reminderAarsagFritekst: reminderFreeText,
            sendReminderEmail,
        },
        {
            baseURL: getBaseURL(),
            skipBaseURLOverride: true,
            useImitatorForSalaryApi: true,
        } as CustomAxiosConfig
    )
    return response.data
}

// /Reminder/SletReminder
const deleteReminder = async ({
    caseId,
    caseRegTime,
    reminderId,
}: {
    caseId: string
    caseRegTime: string
    reminderId: string
}): Promise<any> => {
    const response = await axiosWithImitator.delete('/Reminder/SletReminder', {
        data: {
            sagsId: caseId,
            sagsRegTid: caseRegTime,
            reminderId,
        },
        baseURL: getBaseURL(),
        skipBaseURLOverride: true,
        useImitatorForSalaryApi: true,
    } as CustomAxiosConfig)
    return response.data
}

export type CreateCaseNoteType = {
    caseId: string
    caseRegTime: string
    message: string
}

const createCaseNote = async ({
    caseId,
    caseRegTime,
    message,
}: CreateCaseNoteType): Promise<any> => {
    const response = await axiosWithImitator.post(
        '/Notat/OpretNotat',
        {
            sagsId: caseId,
            sagsRegTid: caseRegTime,
            besked: message,
        },
        {
            baseURL: getBaseURL(),
            skipBaseURLOverride: true,
            useImitatorForSalaryApi: true,
        } as CustomAxiosConfig
    )
    return response.data
}

const deleteCaseNote = async ({
    caseId,
    caseRegTime,
    noteId,
}: {
    caseId: string
    caseRegTime: string
    noteId: string
}): Promise<any> => {
    const response = await axiosWithImitator.delete('/Notat/SletNotat', {
        data: {
            sagsId: caseId,
            sagsRegTid: caseRegTime,
            notatId: noteId,
        },
        baseURL: getBaseURL(),
        skipBaseURLOverride: true,
        useImitatorForSalaryApi: true,
    } as CustomAxiosConfig)
    return response.data
}

// /File/UploadSagsDokument/{sagsId}
const uploadFile = async (file: File, caseId: string): Promise<any> => {
    const formData = new FormData()
    formData.append('file', file)
    // formData.append('sagsId', caseId)

    const response = await axiosWithImitator.post(
        `/File/UploadSagsDokument/${caseId}`,
        formData,
        {
            baseURL: getBaseURL(),
            skipBaseURLOverride: true,
            useImitatorForSalaryApi: true,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        } as CustomAxiosConfig
    )
    return response
}

const downloadFile = async (
    caseNumber: string,
    fileName: string
): Promise<any> => {
    const response = await axiosWithImitator.get(
        `/File/DownloadSagsDokument/${caseNumber}/${fileName}`,
        {
            baseURL: getBaseURL(),
            skipBaseURLOverride: true,
            responseType: 'arraybuffer',
            useImitatorForSalaryApi: true,
        } as CustomAxiosConfig
    )
    return response.data
}

// /OplagsdataConstroller/HentSagsBehandlingStatusLister
const getCaseReportingStatusList = async (): Promise<any> => {
    const response = await axiosWithImitator.get(
        '/OplagsdataConstroller/HentSagsBehandlingStatusLister',
        {
            baseURL: getBaseURL(),
            skipBaseURLOverride: true,
            useImitatorForSalaryApi: true,
        } as CustomAxiosConfig
    )
    return response.data as CaseReportingList
}

// /SagOpdater/OpdaterSagsbehandling
const updateCaseReporting = async ({
    caseId,
    caseRegTime,
    kontaktTilNyansatStatusId,
    medlemsskabStatusId,
    loenindplaceringStatusId,
    contactNewlyHiredDate,
}: {
    caseId: string
    caseRegTime: string
    kontaktTilNyansatStatusId: number
    medlemsskabStatusId: number
    loenindplaceringStatusId: number
    contactNewlyHiredDate?: string
}): Promise<any> => {
    const body: any = {
        sagsId: caseId,
        sagsRegTid: caseRegTime,
        kontaktTilNyansatStatusId,
        medlemsskabStatusId,
        loenindplaceringStatusId,
    }
    if (contactNewlyHiredDate) {
        body.kontaktTilNyansatDato = contactNewlyHiredDate
    }
    const response = await axiosWithImitator.put(
        '/SagOpdater/OpdaterSagsbehandling',
        body,
        {
            baseURL: getBaseURL(),
            skipBaseURLOverride: true,
            useImitatorForSalaryApi: true,
        } as CustomAxiosConfig
    )
    return response
}

const isCaseEmployerMailSent = async (
    caseId: string,
    caseRegTime: string
): Promise<any> => {
    const response = await axiosWithImitator.put(
        '/SagOpdater/TjekAfslutSag',
        {
            sagsId: caseId,
            sagsRegTid: caseRegTime,
        },
        {
            baseURL: getBaseURL(),
            skipBaseURLOverride: true,
            useImitatorForSalaryApi: true,
        } as CustomAxiosConfig
    )
    return response.data
}

const closeCase = async ({
    caseId,
    caseRegTime,
}: {
    caseId: string
    caseRegTime: string
}): Promise<any> => {
    const response = await axiosWithImitator.put(
        '/SagOpdater/AfslutSag',
        {
            sagsId: caseId,
            sagsRegTid: caseRegTime,
        },
        {
            baseURL: getBaseURL(),
            skipBaseURLOverride: true,
            useImitatorForSalaryApi: true,
        } as CustomAxiosConfig
    )
    return response
}

const hasAccessToCase = async (): Promise<any> => {
    const response = await axiosWithImitator.get(
        '/Bruger/HarBrugerLoenindplaceringer',
        {
            baseURL: getBaseURL(),
            skipBaseURLOverride: true,
            useImitatorForSalaryApi: true,
        } as CustomAxiosConfig
    )
    return response
}

export {
    getSalaryData,
    getCaseDetails,
    takeCase,
    getRejectCaseReasonList,
    getClosedCases,
    rejectCase,
    updateCaseMobileNumber,
    updateCaseEmail,
    updateCaseBirthdate,
    updateCaseHireDate,
    tryAttachCase,
    getMailTemplateList,
    sendMail,
    attachCase,
    deleteReminder,
    getReminderReasonList,
    createReminder,
    createCaseNote,
    deleteCaseNote,
    getNextClosedCasesPage,
    uploadFile,
    downloadFile,
    removeMergedCase,
    getCaseReportingStatusList,
    updateCaseReporting,
    closeCase,
    hasAccessToCase,
    isCaseEmployerMailSent,
}
